'use client'

// Ensure all renderers are registered on first import
import './register-renderers'

import { cn } from '@/lib/utils'
import type { SVSPayload, RenderHint, DashboardPayload } from './svs-types'
import { getRenderer } from './registry'
import { TextBlock } from '../blocks/TextBlock'
import { DashboardRenderer } from './renderers/DashboardRenderer'

interface SVSRendererProps {
  /** Raw SVS payload from the server */
  payload: SVSPayload
  className?: string
}

function renderHintClass(hint?: RenderHint): string {
  switch (hint) {
    case 'inline':
      return 'max-w-sm'
    case 'medium':
      return 'max-w-2xl'
    case 'full-width':
      return 'w-full'
    default:
      return 'max-w-2xl'
  }
}

/**
 * Renders a single SVS visualization or falls back to textFallback.
 * Used recursively by DashboardRenderer for composed layouts.
 */
function renderVisualization(payload: SVSPayload, _index: number = 0): React.ReactNode {
  // Dashboard is handled specially for recursive rendering
  if (payload.type === 'dashboard') {
    return (
      <DashboardRenderer
        payload={payload as DashboardPayload}
        renderChild={renderVisualization}
      />
    )
  }

  const Renderer = getRenderer(payload.type)

  if (!Renderer) {
    // Unknown type — render textFallback
    if (payload.textFallback) {
      return <TextBlock content={payload.textFallback} />
    }
    return (
      <p className="text-sm italic text-muted-foreground">
        Visualization type &quot;{payload.type}&quot; is not yet supported.
      </p>
    )
  }

  return <Renderer payload={payload} />
}

/**
 * Top-level SVS renderer with title, caption, and accessibility attributes.
 * This is the component you embed in a chat message.
 */
export function SVSRenderer({ payload, className }: SVSRendererProps) {
  // Version check
  if (payload.version !== '0.1') {
    if (payload.textFallback) {
      return <TextBlock content={payload.textFallback} />
    }
    return (
      <p className="text-sm italic text-muted-foreground">
        Unsupported visualization spec version: {payload.version}
      </p>
    )
  }

  return (
    <div
      className={cn(
        'not-prose my-4 rounded-lg border border-border bg-card p-4',
        renderHintClass(payload.renderHint),
        className,
      )}
      role="figure"
      aria-label={payload.title || payload.description || `${payload.type} visualization`}
      aria-describedby={payload.description ? `svs-desc-${payload.type}` : undefined}
    >
      {/* Title */}
      {payload.title && (
        <h4 className="mb-3 text-sm font-semibold text-card-foreground">{payload.title}</h4>
      )}

      {/* Hidden description for screen readers */}
      {payload.description && (
        <p id={`svs-desc-${payload.type}`} className="sr-only">
          {payload.description}
        </p>
      )}

      {/* Visualization content */}
      {renderVisualization(payload)}

      {/* Caption */}
      {payload.caption && (
        <p className="mt-3 text-xs text-muted-foreground">{payload.caption}</p>
      )}
    </div>
  )
}
